import { FindOptions } from "index.js";
import { Query } from "../query";

export class QueryStats {
    public recentQueries: SingleQueryStats[] = []

    constructor(private _maxNumberOfRecentQueries: number, private _getAutoContext: () => any) {
    }

    public newQuery(location: any, options?: FindOptions) {
        const newStats = new SingleQueryStats(location, options, this._getAutoContext?.())        
        this.addStats(newStats)
        return newStats
    }

    private addStats(query: SingleQueryStats) {
        if (this._maxNumberOfRecentQueries <= 0) {
            return
        }

        this.recentQueries.push(query)

        if (this.recentQueries.length > this._maxNumberOfRecentQueries) {
            this.recentQueries.shift()
        }
    }

    // Note: This is not a setter as it needs to be called from a threaded proxy potentially
    public setMaxNumberOfRecentQueries(maxNumberOfRecentQueries: number) {
        this._maxNumberOfRecentQueries = Math.max(0, maxNumberOfRecentQueries)
    }

    public get maxNumberOfRecentQueries() {
        return this._maxNumberOfRecentQueries
    }

    public clear() {
        this.recentQueries = []
    }

    public toJson({ limit, filter, slowerThan, sort }: { limit?: number, filter?: string, slowerThan?: number, sort?: 'asc'|'desc' } = {}) {

        let q = limit ? this.recentQueries.slice(-limit): this.recentQueries

        if (filter || slowerThan) {
            q = q.filter(q => {
                let filterMatch = true

                if (filter) {
                    // If filter is set we only show queries that match the filter
                    if (q.speed !== filter) {
                        filterMatch = false
                    }
                }

                if (slowerThan) {
                    // If slowerThan is set we only show queries that are slower than the given time
                    if (q.duration < slowerThan) {
                        filterMatch = false
                    }
                }

                return filterMatch
            })
        }

        if (sort) {
            q = [...q].sort((a,b) => {
                if (sort === 'asc') {
                    return a.duration - b.duration
                }

                return b.duration - a.duration
            })
        }

        return '[ ' + q.map(q => q.toJson()).join(', ') + ' ]'
    } 
}

export class SingleQueryStats {
    
    public createdAt: number = Date.now()
    public query: Query;
    public found = 0;
    
    public phases: {
        title: String,
        duration: number,
        begin: number,
        end: number,
        context: any,
        counters: {
            [key: string]: number
        },
        marks: [string, object][],
        opStats: OpStats[],
    }[] = [];

    constructor(
        public location: any,
        public options: FindOptions,
        public context: any
    ) {
        this.phase('query')
    }

    public increment(name: string) {
        if (!this.phases.length) {
            throw new Error('No phases to increment')
        }

        const lastPhase = this.phases[this.phases.length - 1]
        lastPhase.counters[name] = (lastPhase.counters[name] || 0) + 1
    }

    public mark(name: string, info?: object) {
        if (!this.phases.length) {
            throw new Error('No phases to mark')
        }

        const lastPhase = this.phases[this.phases.length - 1]
        lastPhase.marks.push([name, info])
    }

    public newOpStats(title: string) {
        if (!this.phases.length) {
            throw new Error('No phases to opStats to')
        }

        const newOpStats = new OpStats(title);

        const currentPhase = this.phases[this.phases.length - 1]
        if (!currentPhase) {
            currentPhase.opStats.push(newOpStats)
        }
        
        return newOpStats
    }

    public setQuery(query: Query) {
        if (this.query) {
            throw new Error('Query already set')
        }

        this.query = query
    }

    public get began() {
        return this.phases[0]?.begin
    }

    public get ended() {
        return this.phases[this.phases.length - 1]?.end
    }

    public get duration() {
        const begin = this.began
        const end = this.ended

        if (begin && end) {
            return Math.max(0, end - begin)
        }

        return null
    }

    public get speed() {
        if (!this.duration) {
            return 'unfinished'
        }

        return this.duration > 500 ? 'very-slow'
                    : this.duration > 100 ? 'slow'
                    : this.duration > 50 ? 'medium'
                    : 'fast'
    }

    private _endLastPhase() {
        const lastPhase = this.phases[this.phases.length - 1]
        endPhase(lastPhase)
    }

    public phase(title: String, context: any = {}) {
        this.endLastPhase()

        const newPhase = {
            title,
            begin: Date.now(),
            end: null,
            duration: null,
            context,
            counters: {},
            marks: [],
            opStats: [],
        }

        this.phases.push(newPhase)
        return () => endPhase(newPhase)
    }

    private endLastPhase() {
        const lastPhase = this.phases[this.phases.length - 1]
        if (lastPhase && lastPhase.end === null) {
            lastPhase.end = Date.now()
            lastPhase.duration = lastPhase.end - lastPhase.begin
        }
    }

    public close() {
        this.endLastPhase()
    }

    public toJson() {
        return JSON.stringify({
            location: this.location,
            options: this.options,
            context: this.context,
            speed: this.speed,
            duration: this.duration,
            found: this.found,
            query: this.query,
            createdAt: this.createdAt,
            phases: this.phases,
        })
    }
}

function endPhase(phase: { begin: number, end: number, duration: number }) {
    if (phase) {
        phase.end = Date.now()
        phase.duration = phase.end - phase.begin
    }
}

export class OpStats {
    public opCount = 0;
    public context: object = null;

    constructor(public title: string) {}
    
    public beginOp() {
    }

    public endOp() {
        this.opCount++
    }
}

const hrtimer = buildHighResolutionTimer()

function buildHighResolutionTimer()  {
    if (typeof process !== 'undefined' && process.hrtime) {
        return () => process.hrtime.bigint()
    } else if (typeof performance !== 'undefined' && performance.now) {
        return () => BigInt(performance.now() * 1e6)
    } else {
        return () => BigInt(Date.now() * 1e6)
    }
}